import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Separator } from "@akuna/ui";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Badge } from "@/components/ui/badge";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import {
  useCreateExamPrescriptionTemplate,
  useDeleteExamPrescriptionTemplate,
  useExamPrescriptionTemplates,
  useUpdateExamPrescriptionTemplate,
  type ExamPrescriptionTemplateWithItems,
} from "@/hooks/useExamPrescriptionTemplates";
import { useCreateExamType, useExamTypes } from "@/hooks/useExamTypes";
import {
  examPrescriptionTemplateSchema,
  type ExamPrescriptionItemInput,
  type ExamPrescriptionTemplateInput,
} from "@/lib/validations/exam-prescription";
import { cn } from "@/lib/utils";

const buildDefaultItem = (): ExamPrescriptionItemInput => ({
  exam_type_id: "",
  exam_name: "",
  instructions: "",
});

interface ExamPrescriptionTemplatesTabProps {
  selectedTemplateId?: string | null;
  onTemplateApplied?: () => void;
  showTemplatesList?: boolean;
}

export const ExamPrescriptionTemplatesTab = ({
  selectedTemplateId,
  onTemplateApplied,
  showTemplatesList = true,
}: ExamPrescriptionTemplatesTabProps) => {
  const { t } = useTranslation("prescriptions");
  const {
    data: clinicId,
  } = useCurrentClinicId();

  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
  } = useExamPrescriptionTemplates(clinicId);

  const createTemplate = useCreateExamPrescriptionTemplate();
  const updateTemplate = useUpdateExamPrescriptionTemplate();
  const deleteTemplate = useDeleteExamPrescriptionTemplate();

  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<ExamPrescriptionTemplateWithItems | null>(null);
  const [examSearch, setExamSearch] = useState("");

  const { data: examTypes = [], isLoading: isLoadingExamTypes } = useExamTypes({
    searchTerm: examSearch,
    enabled: Boolean(clinicId),
  });
  const createExamType = useCreateExamType();

  const examTypesById = useMemo(
    () => new Map(examTypes.map((exam) => [exam.id, exam])),
    [examTypes],
  );

  const examTypeOptions = useMemo(
    () =>
      examTypes.map((exam) => ({
        label: exam.name,
        value: exam.id,
      })),
    [examTypes],
  );

  const form = useForm<ExamPrescriptionTemplateInput>({
    resolver: zodResolver(examPrescriptionTemplateSchema),
    defaultValues: {
      title: "",
      observations: "",
      items: [buildDefaultItem()],
    },
  });

  const {
    fields: itemFields,
    append,
    remove,
    replace,
    update: updateItem,
  } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const resetForm = () => {
    form.reset({
      title: "",
      observations: "",
      items: [buildDefaultItem()],
    });
    replace([buildDefaultItem()]);
    setEditingTemplateId(null);
    setExamSearch("");
  };

  const handleCreateExamType = async (name: string, index: number) => {
    const created = await createExamType.mutateAsync({ name });
    updateItem(index, {
      ...form.getValues(`items.${index}`),
      exam_type_id: created.id,
      exam_name: created.name,
    });
    setExamSearch("");
  };

  const mapTemplateItems = (template: ExamPrescriptionTemplateWithItems): ExamPrescriptionItemInput[] => {
    if (!template.items.length) return [buildDefaultItem()];

    return template.items.map((item) => ({
      exam_type_id: item.exam_type_id ?? "",
      exam_name: item.exam_name ?? "",
      instructions: item.instructions ?? "",
    }));
  };

  const handleEditTemplate = useCallback((template: ExamPrescriptionTemplateWithItems) => {
    const mappedItems = mapTemplateItems(template);
    form.reset({
      title: template.title ?? "",
      observations: template.observations ?? "",
      items: mappedItems,
    });
    replace(mappedItems);
    setEditingTemplateId(template.id);
    setExamSearch("");
  }, [form, replace]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) return;
    handleEditTemplate(template);
    onTemplateApplied?.();
  }, [handleEditTemplate, selectedTemplateId, templates, onTemplateApplied]);

  const handleSubmit = async (values: ExamPrescriptionTemplateInput) => {
    if (!clinicId) return;

    if (editingTemplateId) {
      await updateTemplate.mutateAsync({
        templateId: editingTemplateId,
        clinicId,
        payload: values,
      });
    } else {
      await createTemplate.mutateAsync({ ...values, clinic_id: clinicId });
    }

    resetForm();
  };

  return (
    <div className={cn(showTemplatesList ? "grid gap-6 md:grid-cols-[2fr,1fr] md:items-start" : "space-y-6")}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 order-last md:order-first">
          <Card>
            <CardHeader>
              <CardTitle>{t("templates.examTitle")}</CardTitle>
              <CardDescription>{t("templates.examDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 mb-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("templates.templateTitle")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("templates.examTitlePlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                {itemFields.map((item, index) => (
                  <div
                    key={item.id}
                    className="rounded-lg border border-dashed border-border/70 p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-3 flex-1">
                        <Controller
                          control={form.control}
                          name={`items.${index}.exam_name`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("templates.exam")}</FormLabel>
                              <FormControl>
                                <Autocomplete
                                  options={examTypeOptions}
                                  value={field.value ?? ""}
                                  onChange={(value) => {
                                    setExamSearch(value);
                                    const currentItem = form.getValues(`items.${index}`);
                                    if (currentItem.exam_type_id) {
                                      updateItem(index, {
                                        ...currentItem,
                                        exam_type_id: "",
                                        exam_name: value,
                                      });
                                    } else {
                                      field.onChange(value);
                                    }
                                  }}
                                  onSelect={(option) => {
                                    const selected = examTypesById.get(option.value);
                                    if (!selected) return;
                                    field.onChange(option.label);
                                    updateItem(index, {
                                      ...form.getValues(`items.${index}`),
                                      exam_type_id: selected.id,
                                      exam_name: selected.name,
                                    });
                                  }}
                                  selectedValue={form.watch(`items.${index}.exam_type_id`) ?? ""}
                                  placeholder={t("templates.examPlaceholder")}
                                  emptyMessage={
                                    isLoadingExamTypes
                                      ? t("examComposer.loadingExams")
                                      : t("examComposer.emptyExams")
                                  }
                                  footer={(() => {
                                    const value = (field.value ?? "").trim();
                                    if (!value) return null;
                                    const hasExactMatch = examTypes.some(
                                      (exam) => exam.name.toLowerCase().trim() === value.toLowerCase(),
                                    );
                                    if (hasExactMatch) return null;
                                    return (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-start text-primary hover:bg-accent"
                                        disabled={createExamType.isPending}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleCreateExamType(value, index);
                                        }}
                                      >
                                        {t("examComposer.createExam", { value })}
                                      </Button>
                                    );
                                  })()}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`items.${index}.instructions`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("templates.instructionsOptional")}</FormLabel>
                              <FormControl>
                                <Textarea rows={2} placeholder={t("templates.instructionsPlaceholder")} {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      {itemFields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-muted-foreground"
                          onClick={() => remove(index)}
                          disabled={itemFields.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                <Button type="button" variant="outline" className="w-full" onClick={() => append(buildDefaultItem())}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("templates.actions.addExam")}
                </Button>
              </div>

              <FormField
                control={form.control}
                name="observations"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel>{t("templates.notes")}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder={t("templates.notesPlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {editingTemplateId ? (
              <Button
                type="button"
                variant="outline"
                className="shadow-soft-search"
                onClick={resetForm}
                disabled={!clinicId}
              >
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("templates.actions.cancelEdit")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="shadow-soft-search"
                onClick={resetForm}
              >
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("templates.actions.clear")}
              </Button>
            )}

            <Button
              type="submit"
              className="w-full sm:w-auto"
              disabled={!clinicId || createTemplate.isPending || updateTemplate.isPending}
            >
              {(createTemplate.isPending || updateTemplate.isPending) ? (
                <Loader2 className="!h-5 !w-5 animate-spin" />
              ) : (
                <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
              )}
              <span className="font-normal">
                {editingTemplateId ? t("templates.actions.update") : t("templates.actions.save")}
              </span>
            </Button>
          </div>
        </form>
      </Form>

      {showTemplatesList && (
        <Card className="order-first md:order-last">
          <CardHeader>
            <CardTitle>{t("templates.registeredTemplates")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 md:max-h-[500px] overflow-y-auto">
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("composer.loadingTemplates")}
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("templates.empty.exam")}
              </p>
            ) : (
              <div className="space-y-3 text-sm">
                {templates.map((template, index) => (
                  <div key={template.id} className="rounded-md border border-border/70 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium">{template.title}</div>
                          <Badge variant="secondary">{t("composer.tabs.exam")}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t("templates.counts.exams", { count: template.items.length })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditTemplate(template)}
                          aria-label={t("templates.editTemplate")}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setTemplateToDelete(template)}
                          aria-label={t("templates.deleteTemplate")}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {template.items.length > 0 && (
                      <>
                        <Separator className="my-2" />
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          {template.items.slice(0, 3).map((item) => (
                            <li key={item.id}>{item.exam_name}</li>
                          ))}
                        </ul>
                        {template.items.length > 3 && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t("templates.counts.moreItems", { count: template.items.length - 3 })}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmationDialog
        open={!!templateToDelete}
        onOpenChange={(open) => {
          if (!open) setTemplateToDelete(null);
        }}
        title={t("templates.actions.deleteTitle")}
        description={t("templates.actions.deleteDescription", { title: templateToDelete?.title ?? "" })}
        confirmText={t("templates.actions.delete")}
        onConfirm={async () => {
          if (!templateToDelete || !clinicId) return;
          await deleteTemplate.mutateAsync({ templateId: templateToDelete.id, clinicId });
          if (editingTemplateId === templateToDelete.id) {
            resetForm();
          }
          setTemplateToDelete(null);
        }}
      />
    </div>
  );
};
